/**
 * auth.js — JWT token management for the CMP frontend.
 *
 * Security model:
 *  - Access token: stored in a module-level JS variable (in-memory only).
 *    Never written to localStorage or sessionStorage to reduce XSS exposure.
 *  - Refresh token: stored in an HttpOnly cookie managed entirely by the
 *    server. The frontend never reads or writes it directly; it is sent
 *    automatically by the browser on every request because `withCredentials`
 *    is set to true on the Axios instance.
 */

import axios from 'axios';
import { getApiBaseUrl } from './apiBaseUrl';

// ---------------------------------------------------------------------------
// In-memory access token store
// ---------------------------------------------------------------------------

/** @type {string|null} */
let _accessToken = null;

/**
 * Returns the current in-memory access token, or null if not set.
 * @returns {string|null}
 */
export function getAccessToken() {
  return _accessToken;
}

/**
 * Stores the access token in memory.
 * The refresh token is handled server-side via HttpOnly cookie — do not pass
 * it here.
 *
 * @param {{ access: string }} tokens
 */
export function setTokens({ access }) {
  _accessToken = access;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-state-change'));
  }
}

const _SESSION_STORAGE_KEYS = [
  'cmp_role',
  'cmp_account_status',
  'cmp_kyc_complete',
  'cmp_anonymous_id',
  'cmp_unread_notifications',
];

/**
 * Persist session metadata returned by login/refresh alongside the access token.
 * @param {Record<string, unknown>} data
 */
export function persistSessionMetadata(data = {}) {
  if (typeof window === 'undefined') return;
  if (data.role) localStorage.setItem('cmp_role', String(data.role));
  if (data.account_status) localStorage.setItem('cmp_account_status', String(data.account_status));
  if (data.kyc_complete !== undefined) {
    localStorage.setItem('cmp_kyc_complete', String(Boolean(data.kyc_complete)));
  }
  if (data.anonymous_id) localStorage.setItem('cmp_anonymous_id', String(data.anonymous_id));
}

/**
 * Clears the in-memory access token (e.g. on logout or failed refresh).
 */
export function clearTokens() {
  _accessToken = null;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-state-change'));
  }
}

/**
 * Wipe both the in-memory access token and persisted session metadata.
 */
export function clearSession() {
  clearTokens();
  if (typeof window === 'undefined') return;
  _SESSION_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

/**
 * Pre-configured Axios instance for all CMP API calls.
 *
 * - baseURL is read from the REACT_APP_API_BASE_URL env variable so it can
 *   be overridden per environment without code changes.
 * - withCredentials: true ensures the browser sends the HttpOnly refresh-token
 *   cookie on every request (required for the token-refresh flow).
 */
export const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// Request interceptor — attach access token
// ---------------------------------------------------------------------------

apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor — silent token refresh on 401
// ---------------------------------------------------------------------------

/**
 * Flag that prevents an infinite refresh loop.
 * Set to true while a refresh request is in-flight; reset afterwards.
 */
let _isRefreshing = false;

/**
 * Queue of callbacks waiting for the refresh to complete.
 * Each entry is { resolve, reject } from a Promise created for the retried
 * request.
 */
let _refreshQueue = [];

/** Auth endpoints that may legitimately return 401 — never trigger silent refresh. */
const _NO_REFRESH_PATHS = [
  '/api/v1/auth/login/',
  '/api/v1/auth/register/',
  '/api/v1/auth/logout/',
  '/api/v1/auth/password/forgot/',
  '/api/v1/auth/password/reset/',
  '/api/v1/auth/email/verify/',
  '/api/v1/auth/email/resend-by-email/',
];

function _shouldSkipTokenRefresh(url = '') {
  return _NO_REFRESH_PATHS.some((path) => url.includes(path));
}

/** Resolve all queued requests with the new access token. */
function _processQueue(error, token = null) {
  _refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  _refreshQueue = [];
}

apiClient.interceptors.response.use(
  // Pass successful responses straight through.
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    // Only attempt a refresh for 401 responses that haven't already been
    // retried (the _retry flag prevents infinite loops for the retry itself).
    // Also skip the refresh endpoint itself to prevent infinite loops.
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/api/v1/auth/token/refresh/') ||
      _shouldSkipTokenRefresh(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    if (_isRefreshing) {
      // Another refresh is already in-flight — queue this request and wait.
      return new Promise((resolve, reject) => {
        _refreshQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    // Mark this request as retried so a second 401 won't trigger another
    // refresh attempt.
    originalRequest._retry = true;
    _isRefreshing = true;

    try {
      // Use base axios to avoid interceptor infinite loop deadlock
      const baseURL = getApiBaseUrl();
      const { data } = await axios.post(`${baseURL}/api/v1/auth/token/refresh/`, {}, { withCredentials: true });

      const newAccessToken = data.access;
      setTokens({ access: newAccessToken });
      persistSessionMetadata(data);

      // Unblock any requests that were queued while the refresh was running.
      _processQueue(null, newAccessToken);

      // Retry the original request with the new token.
      originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      // Refresh failed (e.g. refresh token expired or revoked).
      _processQueue(refreshError, null);
      clearSession();

      // Redirect to login so the user can re-authenticate.
      // Don't redirect if already on an auth page to avoid redirect loops.
      const authPaths = ['/login', '/register', '/verify-email', '/forgot-password'];
      if (!authPaths.some((p) => window.location.pathname.startsWith(p))) {
        window.location.href = '/login';
      }

      return Promise.reject(refreshError);
    } finally {
      _isRefreshing = false;
    }
  },
);
